import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { b2bInvoicesApi } from "../api/b2bInvoices";
import type {
  B2BInvoiceFormData,
  LaborCostCheckParams,
} from "../types";

const QK = "b2b-invoices";

export function useB2BInvoices(
  params?: Record<string, string | number | undefined>,
  // Lista jest dostępna tylko dla CEO / księgowości / pracownika B2B — wywołujący
  // spoza tego kręgu (np. dyrektor techniczny na stronie faktur kosztowych)
  // powinien wyłączyć zapytanie zamiast dostawać 403.
  enabled = true,
) {
  return useQuery({
    queryKey: [QK, "list", params],
    queryFn: () => b2bInvoicesApi.list(params),
    staleTime: 0,
    enabled,
  });
}

export function useB2BInvoice(id: string | undefined) {
  return useQuery({
    queryKey: [QK, "detail", id],
    queryFn: () => b2bInvoicesApi.get(id!),
    enabled: !!id,
    staleTime: 0,
  });
}

export function useCreateB2BInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: B2BInvoiceFormData) => b2bInvoicesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK] });
    },
  });
}

export function useUpdateB2BInvoice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<B2BInvoiceFormData>) => b2bInvoicesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK] });
    },
  });
}

export function useDeleteB2BInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => b2bInvoicesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK] });
    },
  });
}

function makeAction(action: (id: string, ...rest: any[]) => Promise<unknown>) {
  return function useAction() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, ...args }: { id: string; rejection_reason?: string }) =>
        action(id, ...(args.rejection_reason !== undefined ? [args.rejection_reason] : [])),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [QK] });
      },
    });
  };
}

export const useSubmitB2BInvoice = makeAction(b2bInvoicesApi.submit);
export const useApproveB2BInvoice = makeAction(b2bInvoicesApi.approve);
export const useRejectB2BInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rejection_reason }: { id: string; rejection_reason: string }) =>
      b2bInvoicesApi.reject(id, rejection_reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
};
export const useMarkBookedB2BInvoice = makeAction(b2bInvoicesApi.markBooked);
export const useMarkTransferredB2BInvoice = makeAction(b2bInvoicesApi.markTransferred);
export const useMarkPaidB2BInvoice = makeAction(b2bInvoicesApi.markPaid);

// ── Dyskusja pod fakturą ─────────────────────────────────────────────────────

export function useB2BInvoiceComments(invoiceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [QK, "comments", invoiceId],
    queryFn: () => b2bInvoicesApi.listComments(invoiceId!),
    enabled: !!invoiceId && enabled,
  });
}

export function useAddB2BInvoiceComment(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ content, mentionIds }: { content: string; mentionIds: string[] }) =>
      b2bInvoicesApi.addComment(invoiceId, content, mentionIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK, "comments", invoiceId] });
    },
  });
}

export function useDeleteB2BInvoiceComment(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      b2bInvoicesApi.deleteComment(invoiceId, commentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QK, "comments", invoiceId] });
    },
  });
}

export function useLaborCostCheck(params: LaborCostCheckParams | null) {
  return useQuery({
    queryKey: [QK, "labor-cost-check", params],
    queryFn: () => b2bInvoicesApi.laborCostCheck(params!),
    enabled: !!params && Number.isFinite(params.year) && Number.isFinite(params.month),
    staleTime: 30_000,
  });
}

/** Miesiące po terminie bez żadnej faktury — ta część licznika w menu, której nie
 *  ma jako wierszy na liście. Mutacje faktur unieważniają [QK], więc po dodaniu
 *  faktury lista odświeża się od razu mimo długiego staleTime. */
export function useMissingB2BInvoicePeriods(enabled: boolean) {
  return useQuery({
    queryKey: [QK, "missing-for-month"],
    queryFn: () => b2bInvoicesApi.missingForMonth(),
    enabled,
    // Sprawdzamy raz na 30 minut — to nie jest hot path.
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
  });
}

/** Okresy dla formularza nowej faktury — miesiące z dniami na B2B (wg historii umów). */
export function useB2BInvoicePeriods() {
  return useQuery({
    queryKey: [QK, "periods"],
    queryFn: () => b2bInvoicesApi.periods(),
    staleTime: 5 * 60_000,
  });
}

export function useB2BActionCount(enabled: boolean) {
  return useQuery({
    queryKey: [QK, "action-count"],
    queryFn: () => b2bInvoicesApi.actionCount(),
    enabled,
    // Badge w sidebarze — odświeżamy co minutę (zmienia się po każdej akcji).
    refetchInterval: 60_000,
  });
}
